import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { dealsApi, companiesApi, contactsApi, usersApi } from '../api';
import { ArrowLeft, Pencil, Save, X, Trash2, Plus, Building2, CheckSquare, UserCircle } from 'lucide-react';
import SearchableSelect from '../components/SearchableSelect';
import TaskModal from '../components/TaskModal';
import dayjs from 'dayjs';

const stageLabels: Record<string, string> = { LEAD: 'Новые', NEGOTIATION: 'Переговоры', PROPOSAL: 'Предложение', AGREEMENT: 'Согласование', WON: 'Успешно', LOST: 'Проиграна' };
const stageBadge: Record<string, string> = { LEAD: 'badge-info', NEGOTIATION: 'badge-warning', PROPOSAL: 'badge-warning', AGREEMENT: 'badge-default', WON: 'badge-success', LOST: 'badge-danger' };
const taskStatusLabel: Record<string, string> = { PENDING: 'Ожидает', IN_PROGRESS: 'В работе', COMPLETED: 'Выполнена', CANCELLED: 'Отменена' };
const taskStatusColor: Record<string, string> = { PENDING: 'badge-warning', IN_PROGRESS: 'badge-info', COMPLETED: 'badge-success', CANCELLED: 'badge-default' };

export default function DealDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [deal, setDeal] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState<any[]>([]);
  const [contactsList, setContactsList] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [showTaskModal, setShowTaskModal] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await dealsApi.getById(id);
      setDeal(res.data);
      setForm({
        title: res.data.title || '',
        description: res.data.description || '',
        companyId: res.data.companyId || '',
        contactIds: res.data.contactIds?.join(', ') || '',
        stage: res.data.stage || 'LEAD',
        amount: res.data.amount || '',
        currency: res.data.currency || 'RUB',
        probability: res.data.probability || 0,
        expectedCloseDate: res.data.expectedCloseDate ? res.data.expectedCloseDate.split('T')[0] : '',
        lossReason: res.data.lossReason || '',
        assignedId: res.data.assignedId || '',
      });
    } catch (e) {
      console.error('Load deal error:', e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [compRes, contRes, usersRes] = await Promise.all([
          companiesApi.list({ limit: 100 }),
          contactsApi.list({ limit: 100 }),
          usersApi.list({ limit: 100 }),
        ]);
        setCompanies(compRes.data.data || []);
        setContactsList(contRes.data.data || []);
        setUsers(usersRes.data.data || []);
      } catch {}
    };
    loadDropdowns();
  }, []);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const data = {
        ...form,
        amount: form.amount ? parseFloat(form.amount) : null,
        probability: typeof form.probability === 'number' ? form.probability : parseInt(String(form.probability)) || 0,
        expectedCloseDate: form.expectedCloseDate || null,
        companyId: form.companyId || null,
        contactIds: form.contactIds ? form.contactIds.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
        assignedId: form.assignedId || null,
      };
      await dealsApi.update(id, data);
      setEditing(false);
      load();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Удалить сделку?')) return;
    try {
      await dealsApi.remove(id);
      navigate('/deals');
    } catch (e) {
      alert('Ошибка удаления');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Загрузка...</div>;
  if (!deal) return <div style={{ padding: 40, textAlign: 'center' }}>Сделка не найдена</div>;

  const contactNames = deal.contactIds?.length
    ? deal.contactIds.map((cid: string) => {
        const c = contactsList.find((cl: any) => cl.id === cid);
        return c ? { id: c.id, name: `${c.firstName} ${c.lastName}` } : null;
      }).filter(Boolean)
    : [];

  return (
    <div>
      <div className="detail-header">
        <button className="detail-back-btn" onClick={() => navigate('/deals')}>
          <ArrowLeft size={20} />
        </button>
        <div className="detail-header-info">
          <div className="detail-header-title">{deal.title}</div>
          <div className="detail-header-subtitle">
            <span className={`badge ${stageBadge[deal.stage] || 'badge-default'}`}>{stageLabels[deal.stage] || deal.stage}</span>
            {deal.amount && <span style={{ marginLeft: 8, fontWeight: 600 }}>{Number(deal.amount).toLocaleString('ru-RU')} {deal.currency === 'RUB' ? '\u20BD' : deal.currency}</span>}
          </div>
        </div>
        <div className="detail-header-actions">
          {editing ? (
            <>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                <Save size={16} /> {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(false); load(); }}>
                <X size={16} /> Отмена
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
                <Pencil size={16} /> Редактировать
              </button>
              <button className="btn btn-danger btn-sm" onClick={handleDelete}>
                <Trash2 size={16} /> Удалить
              </button>
            </>
          )}
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-grid-main">
          <div className="card">
            <div className="card-header">
              <div className="card-title">Информация о сделке</div>
            </div>
            <div className="card-body">
              {editing ? (
                <>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <label className="form-label">Название *</label>
                      <input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
                    </div>
                  </div>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <SearchableSelect
                        label="Компания"
                        value={form.companyId}
                        onChange={val => setForm({ ...form, companyId: val })}
                        options={companies.map((c: any) => ({ value: c.id, label: c.name }))}
                        emptyLabel="— Без компании —"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Стадия</label>
                      <select className="form-select" value={form.stage} onChange={e => setForm({ ...form, stage: e.target.value })}>
                        {Object.entries(stageLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <label className="form-label">Сумма</label>
                      <input className="form-input" type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Валюта</label>
                      <select className="form-select" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
                        <option value="RUB">RUB</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </div>
                  </div>
                  <div className="detail-edit-row">
                    <div className="form-group">
                      <label className="form-label">Вероятность (%)</label>
                      <input className="form-input" type="number" min={0} max={100} value={form.probability} onChange={e => setForm({ ...form, probability: parseInt(e.target.value) || 0 })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Ожидаемая дата закрытия</label>
                      <input className="form-input" type="date" value={form.expectedCloseDate} onChange={e => setForm({ ...form, expectedCloseDate: e.target.value })} />
                    </div>
                  </div>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <label className="form-label">Контакты (ID через запятую)</label>
                      <input className="form-input" value={form.contactIds} onChange={e => setForm({ ...form, contactIds: e.target.value })} placeholder="ID контактов через запятую" />
                    </div>
                  </div>
                  {form.stage === 'LOST' && (
                    <div className="detail-edit-row single">
                      <div className="form-group">
                        <label className="form-label">Причина проигрыша</label>
                        <textarea className="form-input" rows={2} value={form.lossReason} onChange={e => setForm({ ...form, lossReason: e.target.value })} />
                      </div>
                    </div>
                  )}
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <SearchableSelect
                        label="Ответственный"
                        value={form.assignedId}
                        onChange={val => setForm({ ...form, assignedId: val })}
                        options={users.map((u: any) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))}
                        emptyLabel="— Не назначен —"
                      />
                    </div>
                  </div>
                  <div className="detail-edit-row single">
                    <div className="form-group">
                      <label className="form-label">Описание</label>
                      <textarea className="form-input" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                    </div>
                  </div>
                </>
              ) : (
                <div className="detail-field-grid">
                  <div className="detail-field">
                    <div className="detail-field-label">Название</div>
                    <div className="detail-field-value">{deal.title}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Стадия</div>
                    <div className="detail-field-value"><span className={`badge ${stageBadge[deal.stage] || 'badge-default'}`}>{stageLabels[deal.stage] || deal.stage}</span></div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Компания</div>
                    <div className="detail-field-value">
                      {deal.company ? <Link to={`/companies/${deal.company.id}`} className="detail-link">{deal.company.name}</Link> : <span className="empty">—</span>}
                    </div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Ответственный</div>
                    <div className="detail-field-value">{deal.assigned ? `${deal.assigned.firstName} ${deal.assigned.lastName}` : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Сумма</div>
                    <div className="detail-field-value" style={{ fontWeight: 600 }}>
                      {deal.amount ? `${Number(deal.amount).toLocaleString('ru-RU')} ${deal.currency === 'RUB' ? '\u20BD' : deal.currency}` : <span className="empty">—</span>}
                    </div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Вероятность</div>
                    <div className="detail-field-value">
                      <div className="flex items-center gap-2">
                        <div style={{ width: 60, background: '#eee', height: 6, borderRadius: 3 }}>
                          <div style={{ width: `${deal.probability}%`, background: 'var(--color-primary)', height: 6, borderRadius: 3 }} />
                        </div>
                        <span>{deal.probability}%</span>
                      </div>
                    </div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Ожидаемая дата закрытия</div>
                    <div className="detail-field-value">{deal.expectedCloseDate ? dayjs(deal.expectedCloseDate).format('DD.MM.YYYY') : <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Фактическая дата закрытия</div>
                    <div className="detail-field-value">{deal.actualCloseDate ? dayjs(deal.actualCloseDate).format('DD.MM.YYYY') : <span className="empty">—</span>}</div>
                  </div>
                  {deal.lossReason && (
                    <div className="detail-field detail-field-full">
                      <div className="detail-field-label">Причина проигрыша</div>
                      <div className="detail-field-value">{deal.lossReason}</div>
                    </div>
                  )}
                  {contactNames.length > 0 && (
                    <div className="detail-field detail-field-full">
                      <div className="detail-field-label">Контакты</div>
                      <div className="detail-field-value">
                        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                          {contactNames.map((c: any) => c && (
                            <Link key={c.id} to={`/contacts/${c.id}`} className="detail-link" style={{ fontSize: 13 }}>
                              <UserCircle size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                              {c.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="detail-field detail-field-full">
                    <div className="detail-field-label">Описание</div>
                    <div className="detail-field-value">{deal.description || <span className="empty">—</span>}</div>
                  </div>
                  <div className="detail-field">
                    <div className="detail-field-label">Создано</div>
                    <div className="detail-field-value">{dayjs(deal.createdAt).format('DD.MM.YYYY HH:mm')}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="detail-grid-sidebar">
          {deal.company && (
            <div className="card">
              <div className="card-header">
                <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Building2 size={16} /> Компания
                </div>
              </div>
              <div className="card-body" style={{ padding: 12 }}>
                <Link to={`/companies/${deal.company.id}`} className="detail-related-item">
                  <div className="detail-related-item-info">
                    <div className="detail-related-item-title">{deal.company.name}</div>
                    <div className="detail-related-item-meta">{deal.company.industry || deal.company.type || '—'}</div>
                  </div>
                </Link>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckSquare size={16} /> Задачи ({deal.tasks?.length || 0})
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowTaskModal(true)}>
                <Plus size={14} /> Добавить
              </button>
            </div>
            <div className="card-body" style={{ padding: deal.tasks?.length ? 12 : 24 }}>
              {deal.tasks?.length ? deal.tasks.map((t: any) => (
                <Link key={t.id} to={`/tasks/${t.id}`} className="detail-related-item">
                  <div className="detail-related-item-info">
                    <div className="detail-related-item-title" style={{ textDecoration: t.isCompleted ? 'line-through' : 'none' }}>{t.title}</div>
                    <div className="detail-related-item-meta">
                      {t.dueDate ? dayjs(t.dueDate).format('DD.MM.YYYY') : '—'} · {t.assignee?.firstName} {t.assignee?.lastName}
                    </div>
                  </div>
                  <div className="detail-related-item-badge">
                    <span className={`badge ${taskStatusColor[t.status] || 'badge-default'}`}>{taskStatusLabel[t.status] || t.status}</span>
                  </div>
                </Link>
              )) : <div className="detail-related-empty">Нет задач</div>}
            </div>
          </div>
        </div>
      </div>

      {showTaskModal && (
        <TaskModal
          dealId={id}
          companyId={deal.companyId || undefined}
          onClose={() => setShowTaskModal(false)}
          onSave={() => { setShowTaskModal(false); load(); }}
        />
      )}
    </div>
  );
}
